"use client";

import { useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import {
  ChevronRight, ChevronLeft, Check, Shield, ShieldCheck, AlertTriangle,
  Upload, FileText, User, Briefcase, ClipboardCheck, Lock, ArrowRight,
  Stethoscope, Flame, Star, Radio, Zap, Info, Clock, CheckCircle2,
  ExternalLink, Eye, AlertCircle, Hash,
} from "lucide-react";
import {
  PATHWAY_CONFIGS, ROLE_LABELS, ROLE_DESCRIPTIONS, ROLE_RISK_LEVELS,
  ROLE_REGISTRATION, VET_LEVEL_LABELS, RISK_LEVEL_COLORS,
  getDocumentsForRole, getCompetenciesForRole, getDeclarationsForRole,
  getOnboardingStages,
  type WorkforcePathway, type WorkforceRole,
} from "@/lib/workforce-pathways";

// ─── Step definitions ────────────────────────────────────────────────────────
const STEPS = [
  { id: "pathway",       label: "Pathway",       icon: Star },
  { id: "role",          label: "Role",           icon: Briefcase },
  { id: "personal",      label: "Personal",       icon: User },
  { id: "professional",  label: "Professional",   icon: Stethoscope },
  { id: "competencies",  label: "Competencies",   icon: ClipboardCheck },
  { id: "documents",     label: "Documents",      icon: Upload },
  { id: "declarations",  label: "Declarations",   icon: Lock },
  { id: "review",        label: "Review",         icon: Check },
] as const;
type StepId = typeof STEPS[number]["id"];

// ─── Form state ───────────────────────────────────────────────────────────────
interface ApplyForm {
  pathway: WorkforcePathway | null;
  role: WorkforceRole | null;
  // Personal
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  niNumber: string;
  address: string;
  postcode: string;
  // Professional
  registrationNumber: string;
  registrationExpiry: string;
  yearsExperience: string;
  currentEmployer: string;
  noticePeriod: string;
  coverStatement: string;
  // Competencies — IDs ticked
  competencyIds: Set<string>;
  // Documents — IDs acknowledged as available
  documentIds: Set<string>;
  // Declarations — IDs agreed
  declarationIds: Set<string>;
}

const BLANK: ApplyForm = {
  pathway: null,
  role: null,
  firstName: "", lastName: "", email: "", phone: "",
  dob: "", niNumber: "", address: "", postcode: "",
  registrationNumber: "", registrationExpiry: "",
  yearsExperience: "", currentEmployer: "", noticePeriod: "", coverStatement: "",
  competencyIds: new Set(),
  documentIds: new Set(),
  declarationIds: new Set(),
};

const PATHWAY_ICONS: Record<WorkforcePathway, React.ReactNode> = {
  clinical:        <Stethoscope className="w-7 h-7" />,
  fire_rescue:     <Flame className="w-7 h-7" />,
  event_ops:       <Star className="w-7 h-7" />,
  command_control: <Radio className="w-7 h-7" />,
  specialist_ops:  <Zap className="w-7 h-7" />,
};

const NOTICE_OPTIONS = ["Immediate","1 week","2 weeks","1 month","3 months","Other"];

// ─── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ stepIndex, total }: { stepIndex: number; total: number }) {
  const pct = Math.round((stepIndex / (total - 1)) * 100);
  return (
    <div className="w-full h-1 bg-slate-800 rounded-full overflow-hidden">
      <div className="h-full bg-gradient-to-r from-blue-600 to-blue-400 rounded-full transition-all duration-500"
        style={{ width: `${pct}%` }} />
    </div>
  );
}

// ─── Step label bar ───────────────────────────────────────────────────────────
function StepBar({ currentStep, stepIndex }: { currentStep: StepId; stepIndex: number }) {
  return (
    <div className="flex items-center gap-1 overflow-x-auto pb-1 scrollbar-none">
      {STEPS.map((s, i) => {
        const done    = i < stepIndex;
        const active  = s.id === currentStep;
        const Icon    = s.icon;
        return (
          <div key={s.id} className="flex items-center gap-1 shrink-0">
            <div className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all",
              active ? "bg-blue-600 text-white" : done ? "bg-slate-700 text-emerald-400" : "text-slate-600",
            )}>
              {done ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
              <span className="hidden sm:inline">{s.label}</span>
            </div>
            {i < STEPS.length - 1 && <ChevronRight className="w-3 h-3 text-slate-700 shrink-0" />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Field component ─────────────────────────────────────────────────────────
function Field({ label, required, hint, error, children }: {
  label: string; required?: boolean; hint?: string; error?: string; children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
        {label} {required && <span className="text-red-400">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[10px] text-slate-600 mt-1">{hint}</p>}
      {error && <p className="text-[10px] text-red-400 mt-1 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{error}</p>}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 text-sm bg-slate-800/80 border border-slate-700 rounded-lg text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-600 focus:ring-1 focus:ring-blue-600/30 transition-colors";
const selectCls = inputCls + " cursor-pointer";

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ApplyPage() {
  const router = useRouter();
  const [form, setForm] = useState<ApplyForm>(BLANK);
  const [step, setStep] = useState<StepId>("pathway");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  const stepIndex = STEPS.findIndex(s => s.id === step);
  const set = (field: keyof ApplyForm, value: unknown) => {
    setForm(p => ({ ...p, [field]: value }));
    setErrors(p => { const n = { ...p }; delete n[field as string]; return n; });
  };
  const toggleSet = (field: "competencyIds" | "documentIds" | "declarationIds", id: string) => {
    const s = new Set(form[field]);
    s.has(id) ? s.delete(id) : s.add(id);
    set(field, s);
  };

  const pathwayCfg = form.pathway ? PATHWAY_CONFIGS[form.pathway] : null;
  const roleReg    = form.role ? ROLE_REGISTRATION[form.role] : null;
  const documents  = useMemo(() => form.role ? getDocumentsForRole(form.role) : [], [form.role]);
  const competencies = useMemo(() => form.role ? getCompetenciesForRole(form.role) : [], [form.role]);
  const declarations = useMemo(() => form.role ? getDeclarationsForRole(form.role) : [], [form.role]);
  const stages       = useMemo(() => form.role ? getOnboardingStages(form.role) : [], [form.role]);
  const riskLevel    = form.role ? ROLE_RISK_LEVELS[form.role] : null;
  const vetLevel     = form.role ? VET_LEVEL_LABELS[/* ROLE_VET_LEVELS */ "enhanced_dbs"] : null;

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (step === "personal") {
      if (!form.firstName.trim()) e.firstName = "Required";
      if (!form.lastName.trim()) e.lastName = "Required";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
      if (!form.phone.trim()) e.phone = "Required";
      if (!form.dob) e.dob = "Required";
      if (!form.address.trim()) e.address = "Required";
      if (!form.postcode.trim()) e.postcode = "Required";
    }
    if (step === "professional") {
      if (!form.currentEmployer.trim()) e.currentEmployer = "Required";
      if (!form.coverStatement.trim()) e.coverStatement = "Required";
      if (roleReg && !form.registrationNumber.trim()) e.registrationNumber = `${roleReg.body} registration number required`;
    }
    if (step === "declarations") {
      declarations.filter(d => d.required).forEach(d => {
        if (!form.declarationIds.has(d.id)) e[`decl_${d.id}`] = "You must agree to this declaration";
      });
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function next() {
    if (!validate()) return;
    const idx = STEPS.findIndex(s => s.id === step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].id);
  }
  function back() {
    const idx = STEPS.findIndex(s => s.id === step);
    if (idx > 0) setStep(STEPS[idx - 1].id);
  }

  async function submit() {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await fetch("/api/applications", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName:          form.firstName,
          lastName:           form.lastName,
          email:              form.email,
          phone:              form.phone    || undefined,
          address:            form.address  || undefined,
          postcode:           form.postcode || undefined,
          role:               form.role     ?? undefined,
          registrationNumber: form.registrationNumber || undefined,
          yearsExperience:    form.yearsExperience ? parseInt(form.yearsExperience, 10) : undefined,
          currentEmployer:    form.currentEmployer  || undefined,
          coverNote:          form.coverStatement   || undefined,
          ref:                refNumber,
        }),
      });
    } catch {
      // Non-fatal — still redirect to success so applicant isn't blocked
    }
    router.push("/apply/success");
  }

  const refNumber = useMemo(() => `APP-${new Date().getFullYear()}-${String(Date.now()).slice(-5)}`, []);

  return (
    <div className="min-h-screen" style={{ background: "#080f1a" }}>
      {/* ── Progress Header ────────────────────────────────────────────── */}
      <div className="border-b border-slate-800/80 bg-slate-900/60 px-4 py-3 sticky top-0 z-20">
        <div className="max-w-3xl mx-auto space-y-2.5">
          <div className="flex items-center justify-between">
            <StepBar currentStep={step} stepIndex={stepIndex} />
            <span className="text-[10px] font-mono text-slate-500 ml-3 shrink-0">
              {stepIndex + 1}/{STEPS.length}
            </span>
          </div>
          <ProgressBar stepIndex={stepIndex} total={STEPS.length} />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8 pb-24">

        {/* ── STEP: PATHWAY SELECTION ──────────────────────────────────── */}
        {step === "pathway" && (
          <div>
            <div className="mb-8 text-center">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-blue-800 bg-blue-950/40 text-blue-400 text-[10px] font-bold uppercase tracking-widest mb-4">
                <ShieldCheck className="w-3 h-3" />Workforce Governance Portal
              </div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-white">
                Join the OpsCom Network
              </h1>
              <p className="mt-2 text-slate-400 text-sm max-w-xl mx-auto leading-relaxed">
                Select your workforce pathway. Your application will be dynamically configured to your role type, regulatory requirements, and operational compliance obligations.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mb-8">
              {(Object.values(PATHWAY_CONFIGS) as typeof PATHWAY_CONFIGS[keyof typeof PATHWAY_CONFIGS][]).map(cfg => (
                <button
                  key={cfg.id}
                  onClick={() => { set("pathway", cfg.id); set("role", null); setStep("role"); }}
                  className={cn(
                    "text-left p-4 rounded-xl border transition-all hover:scale-[1.01] active:scale-100 group",
                    cfg.border, cfg.bg,
                  )}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn("text-2xl mt-0.5 group-hover:scale-110 transition-transform")}>{cfg.icon}</div>
                    <div className="flex-1 min-w-0">
                      <div className={cn("text-sm font-bold mb-1", cfg.color)}>{cfg.label}</div>
                      <div className="text-xs text-slate-400 leading-relaxed">{cfg.description}</div>
                      <div className={cn("text-[10px] font-semibold mt-2 uppercase tracking-wide", cfg.color)}>{cfg.tagline}</div>
                      {cfg.cqcAligned && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[9px] text-blue-400 border border-blue-800/60 bg-blue-950/30 px-1.5 py-0.5 rounded">
                          <Shield className="w-2.5 h-2.5" />CQC Aligned
                        </div>
                      )}
                    </div>
                    <ChevronRight className={cn("w-4 h-4 mt-0.5 shrink-0 group-hover:translate-x-0.5 transition-transform", cfg.color)} />
                  </div>
                </button>
              ))}
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { icon: Shield, label: "UK GDPR Compliant" },
                { icon: ShieldCheck, label: "CQC Aligned" },
                { icon: Lock, label: "ISO 27001 Secure" },
                { icon: FileText, label: "ICO Registered" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-center gap-1.5 text-[10px] text-slate-500 border border-slate-800 rounded-full px-3 py-1">
                  <Icon className="w-3 h-3" />{label}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── STEP: ROLE SELECTION ──────────────────────────────────────── */}
        {step === "role" && pathwayCfg && (
          <div>
            <div className="mb-6">
              <button onClick={() => setStep("pathway")} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 mb-4">
                <ChevronLeft className="w-3.5 h-3.5" />Back to pathways
              </button>
              <div className={cn("inline-flex items-center gap-2 px-3 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest mb-3", pathwayCfg.border, pathwayCfg.color)}>
                {pathwayCfg.icon} {pathwayCfg.label}
              </div>
              <h2 className="text-xl font-bold text-white">Select Your Role</h2>
              <p className="text-sm text-slate-400 mt-1">Your role determines the compliance requirements, documents, and declarations required.</p>
            </div>

            <div className="space-y-2 mb-8">
              {pathwayCfg.roles.map(roleId => {
                const risk = ROLE_RISK_LEVELS[roleId];
                const reg  = ROLE_REGISTRATION[roleId];
                const selected = form.role === roleId;
                return (
                  <button
                    key={roleId}
                    onClick={() => { set("role", roleId); }}
                    className={cn(
                      "w-full text-left p-4 rounded-xl border transition-all",
                      selected ? "border-blue-600 bg-blue-950/40" : "border-slate-800 bg-slate-800/20 hover:border-slate-700",
                    )}>
                    <div className="flex items-start gap-3">
                      <div className={cn("w-5 h-5 rounded-full border-2 mt-0.5 flex items-center justify-center shrink-0 transition-colors",
                        selected ? "border-blue-500 bg-blue-600" : "border-slate-600"
                      )}>
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-0.5">
                          <span className="text-sm font-bold text-slate-200">{ROLE_LABELS[roleId]}</span>
                          <span className={cn("text-[9px] font-bold px-1.5 py-0.5 rounded border uppercase", RISK_LEVEL_COLORS[risk])}>
                            {risk.toUpperCase()}
                          </span>
                          {reg && (
                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded border border-blue-800 bg-blue-950/30 text-blue-400">
                              {reg.body} Required
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 leading-relaxed">{ROLE_DESCRIPTIONS[roleId]}</p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="flex gap-3">
              <button onClick={() => setStep("pathway")} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={() => { if (!form.role) { setErrors({ role: "Please select a role" }); return; } next(); }}
                disabled={!form.role}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 text-white font-bold rounded-xl transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
            {errors.role && <p className="text-xs text-red-400 mt-2">{errors.role}</p>}
          </div>
        )}

        {/* ── STEP: PERSONAL DETAILS ────────────────────────────────────── */}
        {step === "personal" && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Personal Details</h2>
              </div>
              <p className="text-sm text-slate-400">Your personal information is processed securely in accordance with UK GDPR and will be used only for recruitment and employment verification purposes.</p>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Field label="First Name" required error={errors.firstName}>
                  <input type="text" value={form.firstName} onChange={e => set("firstName", e.target.value)} className={inputCls} placeholder="First name" />
                </Field>
                <Field label="Last Name" required error={errors.lastName}>
                  <input type="text" value={form.lastName} onChange={e => set("lastName", e.target.value)} className={inputCls} placeholder="Last name" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Email Address" required error={errors.email}>
                  <input type="email" value={form.email} onChange={e => set("email", e.target.value)} className={inputCls} placeholder="your@email.com" />
                </Field>
                <Field label="Phone Number" required error={errors.phone}>
                  <input type="tel" value={form.phone} onChange={e => set("phone", e.target.value)} className={inputCls} placeholder="07700 000000" />
                </Field>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Date of Birth" required error={errors.dob}>
                  <input type="date" value={form.dob} onChange={e => set("dob", e.target.value)} className={inputCls} />
                </Field>
                <Field label="National Insurance Number" hint="UK NI number — used for employment verification only.">
                  <input type="text" value={form.niNumber} onChange={e => set("niNumber", e.target.value.toUpperCase())} className={inputCls} placeholder="AB 12 34 56 C" maxLength={13} />
                </Field>
              </div>
              <Field label="Address" required error={errors.address}>
                <input type="text" value={form.address} onChange={e => set("address", e.target.value)} className={inputCls} placeholder="Full address including town/city" />
              </Field>
              <Field label="Postcode" required error={errors.postcode}>
                <input type="text" value={form.postcode} onChange={e => set("postcode", e.target.value.toUpperCase())} className={cn(inputCls, "max-w-[180px]")} placeholder="SW1A 1AA" />
              </Field>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={back} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={next} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP: PROFESSIONAL DETAILS ────────────────────────────────── */}
        {step === "professional" && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Professional Details</h2>
              </div>
              {form.role && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-700 bg-slate-800/40 text-xs font-medium text-slate-300">
                  Applying for: <span className="font-bold text-slate-100">{ROLE_LABELS[form.role]}</span>
                </div>
              )}
            </div>

            {/* Registration section — dynamic */}
            {roleReg && (
              <div className="mb-6 p-4 rounded-xl border border-blue-800/60 bg-blue-950/20">
                <div className="flex items-center gap-2 mb-3">
                  <ShieldCheck className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-bold text-blue-300">Professional Registration Required</span>
                </div>
                <p className="text-xs text-slate-400 mb-3">This role requires current registration with the <strong className="text-slate-200">{roleReg.bodyLabel}</strong>. Your registration will be verified.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label={roleReg.numberLabel} required error={errors.registrationNumber}
                    hint={roleReg.verificationUrl ? undefined : undefined}>
                    <div className="flex gap-2">
                      <input type="text" value={form.registrationNumber} onChange={e => set("registrationNumber", e.target.value)} className={inputCls} placeholder={roleReg.body === "HCPC" ? "PA12345" : roleReg.body === "GMC" ? "1234567" : "Enter number"} />
                      {roleReg.verificationUrl && (
                        <a href={roleReg.verificationUrl} target="_blank" rel="noopener noreferrer"
                          className="flex items-center gap-1 px-2.5 py-2 text-[10px] border border-slate-700 rounded-lg text-slate-500 hover:text-blue-400 hover:border-blue-800 transition-colors whitespace-nowrap">
                          <ExternalLink className="w-3 h-3" />Verify
                        </a>
                      )}
                    </div>
                  </Field>
                  {roleReg.expiryRequired && (
                    <Field label="Registration Expiry" required error={errors.registrationExpiry}>
                      <input type="date" value={form.registrationExpiry} onChange={e => set("registrationExpiry", e.target.value)} className={inputCls} />
                    </Field>
                  )}
                </div>
                {roleReg.notes && (
                  <p className="text-[10px] text-slate-500 mt-2 flex items-start gap-1.5">
                    <Info className="w-3 h-3 shrink-0 mt-0.5" />{roleReg.notes}
                  </p>
                )}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Years of Experience" hint="In this role or equivalent.">
                  <select value={form.yearsExperience} onChange={e => set("yearsExperience", e.target.value)} className={selectCls}>
                    <option value="">Select…</option>
                    {["<1 year","1–2 years","2–5 years","5–10 years","10–15 years","15+ years"].map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
                <Field label="Notice Period">
                  <select value={form.noticePeriod} onChange={e => set("noticePeriod", e.target.value)} className={selectCls}>
                    <option value="">Select…</option>
                    {NOTICE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Current / Most Recent Employer" required error={errors.currentEmployer}>
                <input type="text" value={form.currentEmployer} onChange={e => set("currentEmployer", e.target.value)} className={inputCls} placeholder="Organisation name and role" />
              </Field>
              <Field label="Supporting Statement" required error={errors.coverStatement}
                hint="Why are you applying? Highlight relevant experience, specialist skills, and what you can contribute operationally. Minimum 100 words.">
                <textarea value={form.coverStatement} onChange={e => set("coverStatement", e.target.value)}
                  rows={5} placeholder="Describe your experience, skills, and motivation for applying to this role…"
                  className={cn(inputCls, "resize-none")} />
                <div className={cn("text-[10px] mt-1 tabular-nums", form.coverStatement.split(/\s+/).filter(Boolean).length >= 100 ? "text-emerald-400" : "text-slate-600")}>
                  {form.coverStatement.split(/\s+/).filter(Boolean).length} / 100 words minimum
                </div>
              </Field>
            </div>
            <div className="flex gap-3 mt-8">
              <button onClick={back} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={next} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP: COMPETENCIES ────────────────────────────────────────── */}
        {step === "competencies" && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <ClipboardCheck className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Competencies & Qualifications</h2>
              </div>
              <p className="text-sm text-slate-400">Indicate which competencies you currently hold. Evidence will be required during the verification stage. Required competencies are mandatory for deployment.</p>
            </div>
            {competencies.length === 0 ? (
              <div className="text-sm text-slate-500 py-6 text-center">No specific competencies required for this role.</div>
            ) : (
              <div className="space-y-2 mb-6">
                {Object.entries(
                  competencies.reduce<Record<string, typeof competencies>>((acc, c) => {
                    (acc[c.category] = acc[c.category] ?? []).push(c);
                    return acc;
                  }, {})
                ).map(([cat, items]) => (
                  <div key={cat} className="rounded-xl border border-slate-800 overflow-hidden">
                    <div className="px-3 py-2 bg-slate-800/40 border-b border-slate-800">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{cat.replace("_", " ")}</span>
                    </div>
                    <div className="divide-y divide-slate-800/50">
                      {items.map(c => {
                        const checked = form.competencyIds.has(c.id);
                        return (
                          <label key={c.id} className="flex items-start gap-3 p-3 cursor-pointer hover:bg-slate-800/30 transition-colors">
                            <div className={cn(
                              "w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors",
                              checked ? "border-blue-500 bg-blue-600" : "border-slate-600"
                            )}>
                              {checked && <Check className="w-3 h-3 text-white" />}
                            </div>
                            <input type="checkbox" checked={checked} onChange={() => toggleSet("competencyIds", c.id)} className="sr-only" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm text-slate-200">{c.label}</span>
                                {c.required && <span className="text-[9px] text-red-400 border border-red-800/50 bg-red-950/20 px-1 py-0.5 rounded">Required</span>}
                                {c.expiryYears && <span className="text-[9px] text-slate-500 border border-slate-700 px-1 py-0.5 rounded">Expires {c.expiryYears}yr</span>}
                              </div>
                              {c.notes && <p className="text-[10px] text-slate-500 mt-0.5">{c.notes}</p>}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="flex gap-3 mt-2">
              <button onClick={back} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={next} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP: DOCUMENTS ───────────────────────────────────────────── */}
        {step === "documents" && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Upload className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Required Documents</h2>
              </div>
              <p className="text-sm text-slate-400">These documents will be required to complete your application. Indicate which you currently have available. Documents will be collected and verified during the onboarding process.</p>
              <div className="mt-3 flex items-start gap-2 p-3 rounded-lg border border-amber-800/50 bg-amber-950/20">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                <p className="text-xs text-amber-300">Do not upload documents at this stage. You will be contacted with secure upload instructions following application review.</p>
              </div>
            </div>
            <div className="space-y-2 mb-6">
              {documents.map(doc => {
                const checked = form.documentIds.has(doc.id);
                return (
                  <label key={doc.id} className={cn(
                    "flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-all",
                    checked ? "border-emerald-800/60 bg-emerald-950/15" : doc.required ? "border-slate-700 bg-slate-800/20 hover:border-slate-600" : "border-slate-800 bg-slate-800/10 hover:border-slate-700",
                  )}>
                    <div className={cn(
                      "w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors",
                      checked ? "border-emerald-500 bg-emerald-600" : "border-slate-600"
                    )}>
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <input type="checkbox" checked={checked} onChange={() => toggleSet("documentIds", doc.id)} className="sr-only" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-0.5">
                        <span className="text-sm font-semibold text-slate-200">{doc.name}</span>
                        {doc.required
                          ? <span className="text-[9px] font-bold text-red-400 border border-red-800/50 bg-red-950/20 px-1.5 py-0.5 rounded uppercase">Required</span>
                          : <span className="text-[9px] text-slate-500 border border-slate-700 px-1.5 py-0.5 rounded uppercase">Optional</span>
                        }
                        {doc.validityYears && <span className="text-[9px] text-slate-500 border border-slate-700 px-1.5 py-0.5 rounded">Valid {doc.validityYears}yr</span>}
                        {doc.verificationRequired && <span className="text-[9px] text-blue-400 border border-blue-800/50 bg-blue-950/20 px-1.5 py-0.5 rounded flex items-center gap-0.5"><Eye className="w-2.5 h-2.5" />Verified</span>}
                      </div>
                      <p className="text-xs text-slate-400 leading-relaxed">{doc.description}</p>
                      {doc.notes && <p className="text-[10px] text-slate-500 mt-1">{doc.notes}</p>}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="flex gap-3 mt-2">
              <button onClick={back} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={next} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP: DECLARATIONS ────────────────────────────────────────── */}
        {step === "declarations" && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <Lock className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Governance Declarations</h2>
              </div>
              <p className="text-sm text-slate-400">Please read each declaration carefully before agreeing. These are legally significant statements forming part of your application. All declarations are logged with a timestamp against your reference number.</p>
            </div>
            <div className="space-y-3 mb-6">
              {declarations.map(decl => {
                const agreed = form.declarationIds.has(decl.id);
                const errKey = `decl_${decl.id}`;
                return (
                  <div key={decl.id} className={cn(
                    "rounded-xl border p-4 transition-all",
                    agreed ? "border-emerald-800/60 bg-emerald-950/10" : errors[errKey] ? "border-red-800 bg-red-950/10" : "border-slate-700 bg-slate-800/20"
                  )}>
                    <div className="flex items-start gap-3">
                      <label className="flex items-start gap-3 cursor-pointer flex-1">
                        <div className={cn(
                          "w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 mt-0.5 transition-colors",
                          agreed ? "border-emerald-500 bg-emerald-600" : errors[errKey] ? "border-red-600" : "border-slate-600"
                        )}>
                          {agreed && <Check className="w-3 h-3 text-white" />}
                        </div>
                        <input type="checkbox" checked={agreed} onChange={() => toggleSet("declarationIds", decl.id)} className="sr-only" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <span className="text-sm font-bold text-slate-200">{decl.title}</span>
                            {decl.required && <span className="text-[9px] font-bold text-red-400 border border-red-800/50 bg-red-950/20 px-1.5 py-0.5 rounded uppercase">Required</span>}
                            <span className={cn("text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border",
                              decl.type === "gdpr" ? "text-purple-400 border-purple-800/50 bg-purple-950/20" :
                              decl.type === "clinical_governance" ? "text-blue-400 border-blue-800/50 bg-blue-950/20" :
                              decl.type === "safeguarding" ? "text-red-400 border-red-800/50 bg-red-950/20" :
                              "text-slate-400 border-slate-700"
                            )}>{decl.type.replace("_", " ")}</span>
                          </div>
                          <p className="text-xs text-slate-400 leading-relaxed">{decl.text}</p>
                        </div>
                      </label>
                    </div>
                    {errors[errKey] && <p className="text-[10px] text-red-400 mt-2 ml-8 flex items-center gap-1"><AlertCircle className="w-3 h-3" />{errors[errKey]}</p>}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-3 mt-2">
              <button onClick={back} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={next} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-700 hover:bg-blue-600 text-white font-bold rounded-xl transition-colors">
                Continue <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* ── STEP: REVIEW ──────────────────────────────────────────────── */}
        {step === "review" && (
          <div>
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-5 h-5 text-blue-400" />
                <h2 className="text-xl font-bold text-white">Review & Submit</h2>
              </div>
              <p className="text-sm text-slate-400">Please review your application before submission. Your reference number has been pre-assigned below.</p>
            </div>

            {/* Reference */}
            <div className="p-4 rounded-xl border border-blue-800/60 bg-blue-950/20 mb-6">
              <div className="flex items-center gap-2 mb-1">
                <Hash className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold text-blue-400 uppercase tracking-widest">Application Reference</span>
              </div>
              <div className="font-mono text-xl font-black text-white tracking-wider">{refNumber}</div>
              <p className="text-[10px] text-slate-500 mt-1">Retain this reference for all correspondence. It will be confirmed in your email.</p>
            </div>

            {/* Summary table */}
            <div className="space-y-3 mb-6">
              {[
                { label: "Pathway", value: pathwayCfg?.label ?? "—" },
                { label: "Role", value: form.role ? ROLE_LABELS[form.role] : "—" },
                { label: "Name", value: [form.firstName, form.lastName].filter(Boolean).join(" ") || "—" },
                { label: "Email", value: form.email || "—" },
                { label: "Phone", value: form.phone || "—" },
                { label: "Current Employer", value: form.currentEmployer || "—" },
                { label: "Competencies declared", value: `${form.competencyIds.size} of ${competencies.length}` },
                { label: "Documents available", value: `${form.documentIds.size} of ${documents.length}` },
                { label: "Declarations agreed", value: `${form.declarationIds.size} of ${declarations.length}` },
              ].map(row => (
                <div key={row.label} className="flex items-start justify-between gap-4 py-2 border-b border-slate-800/60 last:border-0">
                  <span className="text-xs text-slate-500 font-medium shrink-0">{row.label}</span>
                  <span className="text-xs text-slate-200 font-semibold text-right">{row.value}</span>
                </div>
              ))}
            </div>

            {/* Onboarding pipeline preview */}
            {stages.length > 0 && (
              <div className="mb-6 p-4 rounded-xl border border-slate-700 bg-slate-800/20">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">What Happens Next</span>
                </div>
                <div className="space-y-2">
                  {stages.map((s, i) => (
                    <div key={s.id} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-slate-700 border border-slate-600 text-[9px] flex items-center justify-center font-bold text-slate-400 shrink-0 mt-0.5">{i + 1}</div>
                      <div>
                        <div className="text-xs font-semibold text-slate-300">{s.label}</div>
                        <div className="text-[10px] text-slate-500">{s.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Governance statement */}
            <div className="p-4 rounded-xl border border-slate-800 bg-slate-900/40 mb-6 text-[10px] text-slate-500 space-y-1">
              <p>By submitting this application you confirm that all information provided is accurate and complete.</p>
              <p>Your data is processed under UK GDPR. Reference: <span className="font-mono">{refNumber}</span>.</p>
            </div>

            <div className="flex gap-3">
              <button onClick={back} className="flex items-center gap-2 px-5 py-2.5 border border-slate-700 text-slate-400 hover:text-slate-200 rounded-xl transition-colors">
                <ChevronLeft className="w-4 h-4" />Back
              </button>
              <button onClick={submit} disabled={submitting}
                className="flex-1 flex items-center justify-center gap-2 py-3 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60 text-white font-bold rounded-xl transition-colors text-sm">
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Submitting…</>
                ) : (
                  <><Check className="w-4 h-4" />Submit Application</>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
